/**
 * 代码服务类
 * 实现代码的保存、获取、列表和 AI 助手等功能
 */

// API 常量定义
const CODE_API_BASE_URL = 'https://code-management-api.openaitool.cn/api/code';
const SAVE_API_URL = `${CODE_API_BASE_URL}/save`;
const GET_API_URL = `${CODE_API_BASE_URL}/get`;
const AI_ASSISTANT_API_URL = 'https://ai-assistant-api.openaitool.cn/';

// 用户配置
// 当前用户ID
let currentUserId: string | null = null;

/**
 * 设置当前用户ID
 * @param userId 用户ID
 */
export const setCurrentUserId = (userId: string): void => {
  console.log('设置当前用户ID:', userId);
  currentUserId = userId;
};

/**
 * 获取当前用户ID
 * @returns 当前用户ID或默认ID
 */
const getUserId = (): string => {
  if (!currentUserId) {
    console.warn('未设置用户ID，使用默认ID');
    return '1001'; // 默认ID，仅在未登录时使用
  }
  return currentUserId;
};
const STORAGE_DOC_ID_KEY = 'code_editor_doc_id'; // localStorage 键名

// 类型定义
export interface CodeDocument {
  doc_id: string;
  title: string;
  version: number;
  timestamp: string;
  language?: string;
  content?: string; // 代码内容字段
  previewContent?: string; // 预览内容字段
}

// 保存结果接口
export interface SaveResult {
  success: boolean;
  message: string;
  data?: {
    doc_id: string;
    version: number;
  };
}

// AI 助手请求参数
export interface AiAssistantParams {
  taskType: string;
  studentId: string;
  selection?: string;
  fullCode?: string;
  question: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginationData {
  docs: CodeDocument[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

/**
 * 生成随机的UUID作为文档ID
 * @returns {string} UUID字符串
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * 获取或创建文档ID
 * @returns {string} 文档ID
 */
function getOrCreateDocId(): string {
  let docId = localStorage.getItem(STORAGE_DOC_ID_KEY);
  if (!docId) {
    docId = generateUUID();
    localStorage.setItem(STORAGE_DOC_ID_KEY, docId);
    console.log('已创建新的文档ID:', docId);
  } else {
    console.log('使用现有文档ID:', docId);
  }
  return docId;
}

/**
 * 保存代码到服务器
 * @param {string} content 代码内容
 * @param {string} title 文件标题
 * @returns {Promise<Object>} 保存结果
 */
/**
 * 获取代码列表
 * @param page 页码
 * @param keyword 搜索关键词
 * @param pageSize 每页数量
 * @returns 代码列表数据
 */
async function getCodeList(
  page: number = 1,
  keyword?: string,
  pageSize: number = 6
): Promise<ApiResponse<PaginationData>> {
  try {
    let url = `${CODE_API_BASE_URL}/`;
    
    // 根据是否有关键词决定使用列表 API 还是搜索 API
    if (keyword && keyword.trim() !== '') {
      url += `search?student_id=${getUserId()}&keyword=${encodeURIComponent(keyword)}&page=${page}&pageSize=${pageSize}`;
    } else {
      url += `list?student_id=${getUserId()}&page=${page}&pageSize=${pageSize}`;
    }
    
    console.log('获取代码列表，请求URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: AbortSignal.timeout(10000) // 10秒超时
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('获取代码列表失败:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const data = await response.json();
    
    if (data && data.success) {
      // 处理文档数据，添加预览内容
      const processedDocs = (data.data?.docs || []).map((doc: any) => {
        // 创建预览内容，取前25个字符
        const previewContent = doc.content 
          ? doc.content.slice(0, 25) + (doc.content.length > 25 ? '...' : '')
          : '';
          
        return {
          ...doc,
          title: doc.title || `未命名文档_${doc.doc_id?.slice(0, 6) || 'unknown'}`,
          language: doc.language || 'cpp', // 默认语言为 cpp
          previewContent // 添加预览内容
        };
      });
      
      return {
        ...data,
        data: {
          ...data.data,
          docs: processedDocs
        }
      };
    } else {
      console.error('获取代码列表失败:', data?.message || '未知错误');
      throw new Error(data?.message || '获取代码列表失败');
    }
  } catch (error) {
    console.error('获取代码列表出错:', error);
    throw error;
  }
}

/**
 * 保存代码到服务器
 * @param content 代码内容
 * @param title 文件标题
 * @param language 代码语言
 * @param docId 可选的文档ID，如果不提供将自动生成
 * @returns 保存结果
 */
async function saveCode(
  content: string, 
  title: string, 
  language?: string, 
  docId?: string
): Promise<SaveResult> {
  try {
    if (!content.trim()) {
      console.log('内容为空，跳过保存');
      throw new Error('内容为空');
    }
    
    // 获取或创建文档ID
    const documentId = docId || getOrCreateDocId();
    
    // 准备请求数据
    const requestData: {
      student_id: string;
      doc_id: string;
      title: string;
      content: string;
      language?: string;
    } = {
      student_id: getUserId(),
      doc_id: documentId,
      title: title || '未命名文档',
      content: content
    };
    
    // 如果提供了语言参数，添加到请求数据中
    if (language) {
      requestData.language = language;
    }
    
    console.log('正在保存代码...');
    console.log('请求数据:', requestData);
    
    // 发送请求
    const response = await fetch(SAVE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    // 处理响应
    const result = await response.json();
    
    if (result.success) {
      console.log('保存成功:', result);
      showNotification('保存成功', 'success');
      return result;
    } else {
      console.error('保存失败:', result);
      showNotification(`保存失败: ${result.message || '未知错误'}`, 'error');
      return result;
    }
  } catch (error: any) {
    console.error('保存过程出错:', error);
    const errorMessage = error instanceof Error ? error.message : '保存过程出错';
    showNotification(`保存失败: ${errorMessage}`, 'error');
    throw error;
  }
}

/**
 * 显示通知消息
 * @param {string} message 通知消息
 * @param {string} type 通知类型 ('success', 'error', 'info')
 */
function showNotification(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
  // 创建一个简单的通知元素
  const notification = document.createElement('div');
  notification.className = `notification notification-${type}`;
  notification.textContent = message;
  
  // 样式
  notification.style.position = 'fixed';
  notification.style.top = '20px';
  notification.style.left = '50%';
  notification.style.transform = 'translateX(-50%)';
  notification.style.padding = '12px 24px';
  notification.style.borderRadius = '4px';
  notification.style.zIndex = '9999';
  notification.style.maxWidth = '80%';
  notification.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
  notification.style.fontSize = '14px';
  notification.style.fontWeight = '500';
  notification.style.transition = 'all 0.3s ease';
  notification.style.textAlign = 'center';
  
  // 根据类型设置颜色
  if (type === 'success') {
    notification.style.backgroundColor = '#4CAF50';
    notification.style.color = 'white';
  } else if (type === 'error') {
    notification.style.backgroundColor = '#F44336';
    notification.style.color = 'white';
  } else {
    notification.style.backgroundColor = '#2196F3';
    notification.style.color = 'white';
  }
  
  // 添加到文档
  document.body.appendChild(notification);
  
  // 3秒后自动移除
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.5s';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 500);
  }, 3000);
}

/**
 * 获取指定文档的内容
 * @param fileId 文档ID
 * @returns 文档内容及元数据
 */
async function getCodeFile(fileId: string): Promise<ApiResponse<CodeDocument>> {
  try {
    if (!fileId || fileId.trim() === '') {
      throw new Error('文档ID不能为空');
    }
    
    const url = `${GET_API_URL}?student_id=${getUserId()}&doc_id=${fileId}`;
    console.log('获取文档内容，请求URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      signal: AbortSignal.timeout(10000) // 10秒超时
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('获取文档内容失败:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }
    
    const result = await response.json();
    
    if (result.success) {
      // 确保文档数据完整
      const fileData = {
        ...result.data,
        title: result.data.title || `未命名文档_${fileId.slice(0, 6)}`,
        language: result.data.language || 'cpp' // 默认语言为 cpp
      };
      
      return {
        ...result,
        data: fileData
      };
    } else {
      console.error('获取文档失败:', result.message || '未知错误');
      throw new Error(result.message || '获取文档失败');
    }
  } catch (error) {
    console.error('获取文档出错:', error);
    throw error;
  }
}

/**
 * 使用 AI 助手解释代码
 * @param question 用户提问
 * @param selection 选中的代码片段
 * @param fullCode 完整代码
 * @param taskType 任务类型，默认为 'explain'
 * @returns AI 助手的响应
 */
async function getAiAssistance(
  question: string,
  selection?: string,
  fullCode?: string,
  taskType: string = 'explain'
): Promise<any> {
  try {
    if (!question.trim()) {
      throw new Error('问题不能为空');
    }
    
    const requestData: AiAssistantParams = {
      taskType,
      studentId: getUserId(),
      question
    };
    
    // 添加可选参数
    if (selection) requestData.selection = selection;
    if (fullCode) requestData.fullCode = fullCode;
    
    console.log('请求 AI 助手:', requestData);
    
    const response = await fetch(AI_ASSISTANT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData),
      signal: AbortSignal.timeout(30000) // 30秒超时，AI 可能需要更长的响应时间
    });
    
    if (!response.ok) {
      throw new Error(`API 请求失败: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI 助手请求出错:', error);
    throw error;
  }
}

// 导出所有函数和类型
export {
  saveCode,
  getCodeList,
  getCodeFile,
  getAiAssistance,
  showNotification,
  generateUUID,
  getOrCreateDocId
};
